import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiEnvelope,
  HiLockClosed,
  HiCheckCircle,
  HiExclamationTriangle,
  HiInformationCircle,
} from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import { getUserSettings, changeEmail } from '../../services/settingsService';
import { toast } from '../../utils/toast';

export default function EmailSettings() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [currentEmail, setCurrentEmail] = useState('');

  const [formData, setFormData] = useState({
    newEmail: '',
    confirmEmail: '',
    currentPassword: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (currentUser) {
      loadEmail();
    }
  }, [currentUser]);

  const loadEmail = async () => {
    try {
      setLoading(true);
      const data = await getUserSettings(currentUser.uid);
      setCurrentEmail(data.email);
    } catch (error) {
      toast.error('Failed to load email');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.newEmail)) {
      newErrors.newEmail = 'Invalid email format';
    }

    if (formData.newEmail === currentEmail) {
      newErrors.newEmail = 'New email must be different from current email';
    }

    if (formData.newEmail !== formData.confirmEmail) {
      newErrors.confirmEmail = 'Emails do not match';
    }

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    } else if (formData.currentPassword.length < 6) {
      newErrors.currentPassword = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setChanging(true);
      const result = await changeEmail({
        uid: currentUser.uid,
        currentPassword: formData.currentPassword,
        newEmail: formData.newEmail,
      });

      setFormData({ newEmail: '', confirmEmail: '', currentPassword: '' });
      setShowForm(false);

      // Handle different response scenarios
      if (result.verificationRequired) {
        // Verification email sent
        toast.info(
          `Verification email sent to ${formData.newEmail}! Please check your inbox and click the verification link to complete the email change. Your email has been updated in ${result.updatedCollections.length} collection(s).`,
          { duration: 10000 }
        );
      } else if (result.firestoreOnly) {
        // Firestore updated but Auth couldn't update
        setCurrentEmail(formData.newEmail);
        toast.success(
          `Email updated in ${result.updatedCollections.length} database collection(s)! You can now login with ${formData.newEmail}. Collections updated: ${result.updatedCollections.join(', ')}`,
          { duration: 8000 }
        );
      } else {
        // Complete success
        setCurrentEmail(formData.newEmail);
        const collectionsUpdated = result.updatedCollections.join(', ');
        toast.success(
          `Email updated successfully! Updated ${result.updatedCollections.length} collection(s): ${collectionsUpdated}. Old email (${result.oldEmail}) saved for reference.`,
          { duration: 6000 }
        );
      }
    } catch (error) {
      toast.error(error.message || 'Failed to change email');
    } finally {
      setChanging(false);
    }
  };

  const handleCancel = () => {
    setFormData({ newEmail: '', confirmEmail: '', currentPassword: '' });
    setErrors({});
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white p-12 shadow-lg">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="h-8 w-8 rounded-full border-4 border-blue-600 border-t-transparent"
        />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-lg ring-1 ring-black/5">
      {/* Header */}
      <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white px-6 py-5">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-500/30">
            <HiEnvelope className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-black">Email Settings</h2>
            <p className="text-sm text-gray-600">Update your email address across all systems</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 sm:p-8">
        {/* Info Alert */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4"
        >
          <div className="flex gap-3">
            <HiInformationCircle className="h-5 w-5 flex-shrink-0 text-blue-600" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold">Email Change Process</p>
              <ul className="mt-2 list-inside list-disc space-y-1 text-blue-800">
                <li>Your email will be updated in all relevant collections</li>
                <li>You'll need to re-authenticate with your current password</li>
                <li>All active sessions will remain logged in</li>
                <li>This change is logged for security purposes</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Current Email Display */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-600">Current Email</p>
              <p className="mt-1 text-lg font-bold text-black">{currentEmail}</p>
            </div>
            <HiCheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>

        {/* Change Email Button / Form */}
        <AnimatePresence mode="wait">
          {!showForm ? (
            <motion.button
              key="show-form-btn"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowForm(true)}
              className="w-full rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:bg-blue-700"
            >
              Change Email Address
            </motion.button>
          ) : (
            <motion.form
              key="email-form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              {/* Warning */}
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                <div className="flex gap-3">
                  <HiExclamationTriangle className="h-5 w-5 flex-shrink-0 text-orange-600" />
                  <div className="text-sm text-orange-900">
                    <p className="font-semibold">Important - Multi-Collection Update</p>
                    <p className="mt-1 text-orange-800">
                      This will update your email in ALL relevant collections including: admin,
                      alumni, clients, coaches, moderator, s_admin, and all related data collections
                      (documentBank, teamMembers, reports, etc.)
                    </p>
                    <p className="mt-2 font-semibold text-orange-800">
                      Your old email will be saved in an "oldEmail" field for reference.
                    </p>
                  </div>
                </div>
              </div>

              {/* New Email */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-black">
                  New Email Address <span className="text-red-600">*</span>
                </label>
                <input
                  type="email"
                  value={formData.newEmail}
                  onChange={e => {
                    setFormData({ ...formData, newEmail: e.target.value });
                    setErrors({ ...errors, newEmail: '' });
                  }}
                  className={`w-full rounded-lg border-2 px-4 py-3 text-sm font-medium text-black transition-all placeholder:text-gray-400 focus:outline-none focus:ring-4 ${
                    errors.newEmail
                      ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/10'
                      : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500/10'
                  }`}
                  placeholder="Enter new email address"
                  required
                />
                {errors.newEmail && <p className="mt-2 text-xs text-red-600">{errors.newEmail}</p>}
              </div>

              {/* Confirm Email */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-black">
                  Confirm New Email <span className="text-red-600">*</span>
                </label>
                <input
                  type="email"
                  value={formData.confirmEmail}
                  onChange={e => {
                    setFormData({ ...formData, confirmEmail: e.target.value });
                    setErrors({ ...errors, confirmEmail: '' });
                  }}
                  className={`w-full rounded-lg border-2 px-4 py-3 text-sm font-medium text-black transition-all placeholder:text-gray-400 focus:outline-none focus:ring-4 ${
                    errors.confirmEmail
                      ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/10'
                      : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500/10'
                  }`}
                  placeholder="Confirm new email address"
                  required
                />
                {errors.confirmEmail && (
                  <p className="mt-2 text-xs text-red-600">{errors.confirmEmail}</p>
                )}
              </div>

              {/* Current Password */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-black">
                  Current Password <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <HiLockClosed className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="password"
                    value={formData.currentPassword}
                    onChange={e => {
                      setFormData({ ...formData, currentPassword: e.target.value });
                      setErrors({ ...errors, currentPassword: '' });
                    }}
                    className={`w-full rounded-lg border-2 py-3 pl-10 pr-4 text-sm font-medium text-black transition-all placeholder:text-gray-400 focus:outline-none focus:ring-4 ${
                      errors.currentPassword
                        ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/10'
                        : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500/10'
                    }`}
                    placeholder="Enter your current password"
                    required
                  />
                </div>
                {errors.currentPassword && (
                  <p className="mt-2 text-xs text-red-600">{errors.currentPassword}</p>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  We need your password to verify this change
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 border-t border-gray-200 pt-6">
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCancel}
                  disabled={changing}
                  className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-black transition-all hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={changing}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:bg-blue-700 disabled:opacity-50"
                >
                  {changing ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                      />
                      Updating...
                    </>
                  ) : (
                    <>
                      <HiCheckCircle className="h-4 w-4" />
                      Update Email
                    </>
                  )}
                </motion.button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
